import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  Button,
  ButtonText,
  Select,
  TextArea,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { organizationTagValidator } from '@publishing-engine/domain/validation';
import { ReactNode, useMemo } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { createWorkspaceTag, updateWorkspaceTag } from 'src/api/mutations';
import { fetchTagCategories, fetchWorkspaceJournals } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import { FilterableSelect } from 'src/components/FilterableSelect';
import { Layout } from 'src/components/Layout';
import NavbarDashboard from 'src/components/NavbarDashboard';
import { Overlay } from 'src/components/Overlay';

const CHARACTER_LIMIT = 100;

export type ITagFormData = {
  name: string;
  description: string;
  usage: string;
  submissionContextId?: string;
  scope?: 'global' | 'journal';
  tagColor: string;
  tagCategory: string;
  tagDescription: string;
  tagCategoryId?: string;
};

type Props = {
  mode: 'create' | 'edit';
  onClose: () => void;
  workspaceId: string;
  usage: string;
  headingTitle: string;
  backButtonLabel: string;
  submitButtonLabel: string;
  successToastTitle: string;
  tagId?: string;
  topContent?: ReactNode;
  defaultValues?: Partial<ITagFormData>;
};

const validateRequiredText = (
  value: string | undefined,
  fieldLabel: string
): true | string => {
  if (!value?.length) {
    return `${fieldLabel} is required`;
  }

  if (!value.trim().length) {
    return `${fieldLabel} cannot contain only whitespace`;
  }

  if (value.length >= CHARACTER_LIMIT) {
    return `Max ${CHARACTER_LIMIT} characters are allowed`;
  }

  return true;
};

const TagForm: React.FC<Props> = ({
  mode,
  onClose,
  workspaceId,
  usage,
  headingTitle,
  backButtonLabel,
  submitButtonLabel,
  successToastTitle,
  tagId,
  topContent,
  defaultValues
}) => {
  const methods = useForm<ITagFormData>({
    defaultValues: {
      name: defaultValues?.name || '',
      description: defaultValues?.description || '',
      usage: defaultValues?.usage || usage,
      submissionContextId: defaultValues?.submissionContextId || '',
      scope:
        usage === 'users'
          ? defaultValues?.scope ??
            (defaultValues?.submissionContextId ? 'journal' : 'global')
          : undefined,
      tagCategoryId: defaultValues?.tagCategoryId || '',
      tagCategory: defaultValues?.tagCategory || '',
      tagColor: defaultValues?.tagColor || '',
      tagDescription: defaultValues?.tagDescription || ''
    },
    resolver: zodResolver(organizationTagValidator)
  });

  const {
    formState: { errors },
    handleSubmit,
    watch,
    register,
    setValue
  } = methods;

  const tagColor = watch('tagColor');
  const submissionContextId = watch('submissionContextId');
  const tagCategoryId = watch('tagCategoryId');
  const scope = watch('scope');

  const { data: tagCategories } = useQuery({
    queryKey: ['tag-category-list', { workspaceId }],
    queryFn: () => fetchTagCategories(workspaceId),
    enabled: Boolean(workspaceId)
  });

  const { data: journalData } = useQuery({
    queryKey: ['workspace-journals', { workspaceId }],
    queryFn: () => fetchWorkspaceJournals(workspaceId, 0, 999),
    enabled:
      (usage === 'submissions' || (usage === 'users' && scope === 'journal')) &&
      Boolean(workspaceId)
  });

  const journalOptions = useMemo(
    () =>
      (journalData?.collection ?? []).map(journal => ({
        value: journal.id,
        label: journal.data.name
      })),
    [journalData]
  );

  const tagCategoryOptions = useMemo(
    () =>
      (tagCategories?.collection ?? []).map(option => ({
        value: option.id,
        label: option.name
      })),
    [tagCategories]
  );

  const toast = useToast();
  const createTagMutation = createWorkspaceTag(workspaceId);
  const updateTagMutation = updateWorkspaceTag(workspaceId);

  const isSubmitting =
    createTagMutation.isPending || updateTagMutation.isPending;

  const onSubmit: SubmitHandler<ITagFormData> = async data => {
    const normalizedName = data.name.trim();
    const normalizedDescription = data.description.trim();

    if (mode === 'edit' && !tagId) {
      toast({
        variant: 'error',
        title: 'Tag id is missing'
      });
      return;
    }

    const payload = {
      name: normalizedName,
      description: normalizedDescription,
      usage: data.usage,
      submissionContextId: data.submissionContextId || undefined,
      tagCategoryId: data.tagCategoryId || undefined
    };

    const res =
      mode === 'edit'
        ? await updateTagMutation.mutateAsync({
            ...payload,
            tagId: tagId as string
          })
        : await createTagMutation.mutateAsync(payload);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: successToastTitle
      });

      onClose();
      return;
    }

    toast({
      variant: 'error',
      title: res.message
    });
  };

  return (
    <Overlay>
      <NavbarDashboard />
      <Layout width="100%" paddingY={4}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection={'column'} gap={3} px={{ base: 4, md: 14 }}>
              <ButtonText
                leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                maxWidth="fit-content"
                onClick={onClose}
                variant="secondary"
              >
                {backButtonLabel}
              </ButtonText>
              <Flex>
                <Text textStyle="headingTitle" mb={5}>
                  {headingTitle}
                </Text>
              </Flex>
              {topContent}

              <Flex flexDirection="column" gap={6}>
                {usage === 'users' && (
                  <Flex gap={2} direction={'column'}>
                    <FieldLabel label="Scope" subText="" isRequired />
                    <Select
                      {...register('scope')}
                      onChange={e => {
                        setValue('scope', e.target.value as 'global' | 'journal');
                        if (e.target.value === 'global') {
                          setValue('submissionContextId', '', {
                            shouldValidate: true
                          });
                        }
                      }}
                      value={scope || ''}
                      disabled={mode === 'edit'}
                    >
                      <option value="global">Global</option>
                      <option value="journal">Journal Specific</option>
                    </Select>
                  </Flex>
                )}
                {usage === 'submissions' ||
                (usage === 'users' && scope === 'journal') ? (
                  <Flex gap={2} direction={'column'}>
                    <FieldLabel label="Journal" subText="" isRequired />
                    <FilterableSelect
                      options={journalOptions}
                      value={submissionContextId || ''}
                      onValueChange={value =>
                        setValue('submissionContextId', value, {
                          shouldValidate: true
                        })
                      }
                      placeholder={
                        journalData ? 'Select journal' : 'Loading journals...'
                      }
                      searchPlaceholder="Search journals"
                      noResultsMessage="No journals found"
                      isDisabled={mode === 'edit'}
                      error={errors.submissionContextId?.message}
                    />
                  </Flex>
                ) : null}

                <Flex gap={{ base: 4, md: 10 }}>
                  <Flex flex={1} gap={2} direction={'column'}>
                    <FieldLabel label="Tag Category" subText="" isRequired />
                    <FilterableSelect
                      options={tagCategoryOptions}
                      value={tagCategoryId || ''}
                      onValueChange={value => {
                        const selectedCategory =
                          tagCategories?.collection?.find(
                            option => option.id === value
                          );

                        setValue('tagCategory', selectedCategory?.name || '');
                        setValue(
                          'tagDescription',
                          selectedCategory?.description || ''
                        );
                        setValue('tagColor', selectedCategory?.color || '');
                        setValue('tagCategoryId', value, {
                          shouldValidate: true
                        });
                      }}
                      placeholder={
                        tagCategories
                          ? 'Select tag category'
                          : 'Loading tag categories...'
                      }
                      searchPlaceholder="Search tag categories"
                      noResultsMessage="No tag categories found"
                      error={errors.tagCategoryId?.message}
                    />
                  </Flex>
                </Flex>
                {tagCategoryId && (
                  <Flex direction={'column'}>
                    <Flex gap={2} direction={'column'}>
                      <FieldLabel
                        label="Tag Category Description"
                        subText=""
                        isRequired={false}
                      />
                      <Text>{watch('tagDescription')}</Text>
                    </Flex>
                    <Flex flex={1} gap={2} direction={'column'} mt={6}>
                      <FieldLabel
                        label="Tag Category Color"
                        subText=""
                        isRequired={false}
                      />
                      <Tooltip
                        hasArrow
                        borderRadius={4}
                        placement="top"
                        label={tagColor}
                      >
                        <Box
                          w="30px"
                          h="30px"
                          bg={tagColor}
                          border="1px solid"
                          borderColor={'gray.300'}
                          borderRadius="4px"
                          shadow={'sm'}
                        />
                      </Tooltip>
                    </Flex>
                  </Flex>
                )}

                <Flex gap={2} direction={'column'}>
                  <FieldLabel label="Tag Name" subText="" isRequired />
                  <TextArea
                    placeholder="Enter name here"
                    value={watch('name')}
                    characterLimit={CHARACTER_LIMIT}
                    isInvalid={!!errors.name}
                    errorMessage={errors.name?.message}
                    {...register('name', {
                      validate: value => validateRequiredText(value, 'Tag name')
                    })}
                    resize={'none'}
                    maxH={10}
                  />
                </Flex>
                <Flex gap={2} direction={'column'}>
                  <FieldLabel
                    label="Description"
                    subText="Your explanation of this Tag will be displayed in a tooltip to help users understand what it means and how to use it."
                    isRequired
                  />
                  <TextArea
                    placeholder="Enter description here"
                    value={watch('description')}
                    characterLimit={CHARACTER_LIMIT}
                    isInvalid={!!errors.description}
                    errorMessage={errors.description?.message}
                    {...register('description', {
                      validate: value =>
                        validateRequiredText(value, 'Description')
                    })}
                    resize={'none'}
                    maxH={14}
                  />
                </Flex>
              </Flex>

              <Flex justifyContent="flex-end" flexWrap="wrap" gap={2} mt={8} alignItems="center">
                <Button variant="secondary" onClick={onClose}>
                  Exit Without Saving
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  {submitButtonLabel}
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Layout>
    </Overlay>
  );
};

export default TagForm;
